import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, CreditCard, ExternalLink, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { booksClient, errMsg } from './api';

const BillingPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [billing, setBilling] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const sessionId = params.get('session_id');

  const refresh = async () => {
    try {
      const { data } = await booksClient.get('/billing/me');
      setBilling(data.billing);
      setPlan(data.plan);
    } catch (e) {
      // noop
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    if (sessionId) {
      // Poll status a couple of times to catch webhook lag
      const timer = setInterval(async () => {
        try {
          const { data } = await booksClient.get(`/billing/status/${sessionId}`);
          if (data.payment_status === 'paid') {
            toast.success('Payment received — plan activated!');
            clearInterval(timer);
            refresh();
          }
        } catch { /* noop */ }
      }, 2500);
      setTimeout(() => clearInterval(timer), 20000);
    }
  }, [sessionId]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await booksClient.post('/billing/portal', {
        return_url: window.location.href,
      });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-400" data-testid="billing-loading">Loading…</div>;

  return (
    <div className="mx-auto max-w-3xl p-6" data-testid="books-billing-page">
      <h1 className="text-3xl font-semibold tracking-tight">Billing & subscription</h1>
      <p className="mt-1 text-sm text-slate-500">Manage your plan, card and invoices.</p>

      <div className="mt-6 rounded-2xl border bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Current plan</div>
            <div className="mt-1 flex items-center gap-2">
              <div className="text-2xl font-semibold">{plan?.name || 'Free Trial'}</div>
              <span
                className={
                  'rounded-full px-2.5 py-0.5 text-xs font-medium ' +
                  (billing?.status === 'active'
                    ? 'bg-emerald-100 text-emerald-700'
                    : billing?.status === 'trialing'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-slate-100 text-slate-700')
                }
                data-testid="billing-status-badge"
              >
                {billing?.status || 'trialing'}
              </span>
            </div>
            <div className="mt-1 text-sm text-slate-600">
              {plan?.tagline}
            </div>
          </div>
          <div className="text-right">
            {plan?.monthly_usd > 0 ? (
              <>
                <div className="text-2xl font-semibold">${billing?.interval === 'year' ? plan.yearly_usd : plan.monthly_usd}</div>
                <div className="text-xs text-slate-500">per {billing?.interval || 'month'}</div>
              </>
            ) : (
              <div className="text-lg font-semibold text-emerald-600">Free</div>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
          {(plan?.features || []).slice(0, 6).map((f) => (
            <div key={f} className="flex items-start gap-2 text-sm text-slate-700">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />
              <span>{f}</span>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <Button
            onClick={() => navigate('/books/pricing')}
            className="bg-emerald-600 text-white hover:bg-emerald-500"
            data-testid="billing-change-plan-btn"
          >
            <Sparkles className="mr-1 h-4 w-4" /> Change plan
          </Button>
          {billing?.stripe_subscription_id && (
            <Button
              variant="outline"
              onClick={openPortal}
              disabled={portalLoading}
              data-testid="billing-portal-btn"
            >
              <CreditCard className="mr-1 h-4 w-4" />
              {portalLoading ? 'Opening…' : 'Manage card & invoices'}
              <ExternalLink className="ml-1 h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
